import toast from 'react-hot-toast';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Check, X } from 'lucide-react';

import { Input, PasswordEyeInput } from '@/components/forms';
import { Button, Spinner } from '@/components/ui';
import { useAuthStore } from '@/stores/useAuthStore';
import { ROUTES } from '@/constants/routes';
import { sanitizeEmail } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import type { SignupRequest } from '@/types/auth';

import styles from './Register.module.css';

export function Register() {
  const navigate = useNavigate();
  const { register, isLoading, isLogged } = useAuthStore();
  const getUser = useAuthStore((s) => s.getUser);

  const [formData, setFormData] = useState<SignupRequest>({
    email: '',
    password1: '',
    password2: '',
  });

  useEffect(() => {
    if (isLogged) {
      const user = getUser();
      if (user?.is_staff) {
        navigate(ROUTES.dashboard);
      } else {
        navigate(ROUTES.pendingApproval);
      }
    }
  }, [isLogged, navigate, getUser]);

  const handleFormDataChange = (key: string, value: string) => {
    setFormData((prevState: SignupRequest) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const passwordRequirements = useMemo(() => {
    const password = formData.password1;
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [formData.password1]);

  const validateForm = (formData: SignupRequest) => {
    const { email, password1, password2 } = formData;

    if (!email || !password1 || !password2) {
      return { isValid: false, message: 'Please fill in all fields' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, message: 'Please enter a valid email address' };
    }

    if (!passwordRequirements.minLength)
      return { isValid: false, message: 'Password must be at least 8 characters' };
    if (!passwordRequirements.hasUppercase)
      return { isValid: false, message: 'Password must have an uppercase letter' };
    if (!passwordRequirements.hasLowercase)
      return { isValid: false, message: 'Password must have a lowercase letter' };
    if (!passwordRequirements.hasNumber)
      return { isValid: false, message: 'Password must have a number' };
    if (!passwordRequirements.hasSpecialChar)
      return { isValid: false, message: 'Password must have a special character' };

    if (password1 !== password2) return { isValid: false, message: 'Passwords do not match' };

    return { isValid: true, message: '' };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validation = validateForm(formData);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }

    const sanitizedData: SignupRequest = {
      email: sanitizeEmail(formData.email),
      password1: formData.password1,
      password2: formData.password2,
    };

    try {
      const status = await register(sanitizedData);
      if (status) {
        navigate(ROUTES.verifyEmail);
      }
    } catch (error) {
      toast.error('An error occurred during registration');
      logger.error('Registration error:', error);
    }
  };

  const requirements = [
    { key: 'minLength', label: 'At least 8 characters' },
    { key: 'hasUppercase', label: 'One uppercase letter' },
    { key: 'hasLowercase', label: 'One lowercase letter' },
    { key: 'hasNumber', label: 'One number' },
    { key: 'hasSpecialChar', label: 'One special character' },
  ] as const;

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoRow}>
          <div className={styles.logoIcon}>
            <Search size={16} />
          </div>
          <span className={styles.logoText}>Leads Manager</span>
        </div>

        <h1 className={styles.title}>Create an account</h1>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <Input
              name="email"
              type="email"
              value={formData.email}
              setValue={(v) => handleFormDataChange('email', v)}
              placeholder="you@example.com"
              variant="bordered"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Password</label>
            <PasswordEyeInput
              name="password1"
              value={formData.password1}
              setValue={(v) => handleFormDataChange('password1', v)}
              placeholder="Create a password"
              variant="bordered"
            />
            {formData.password1 && (
              <div className={styles.requirements}>
                {requirements.map(({ key, label }) => {
                  const met = passwordRequirements[key];
                  return (
                    <div key={key} className={`${styles.req} ${met ? styles.reqMet : ''}`}>
                      {met ? <Check size={14} /> : <X size={14} />}
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Confirm Password</label>
            <PasswordEyeInput
              name="password2"
              value={formData.password2}
              setValue={(v) => handleFormDataChange('password2', v)}
              placeholder="Confirm your password"
              variant="bordered"
            />
          </div>

          <Button variant="primary" size="md" type="submit" disabled={isLoading}>
            {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Create Account'}
          </Button>
        </form>

        <div className={styles.footer}>
          <span>Already have an account?</span>
          <button className={styles.footerLink} onClick={() => navigate(ROUTES.login)}>
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
